import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import { ConsoleSurface, SectionHeading, StatusBadge } from "@/shared/ui";
import { ThresholdValueDisplay } from "@/features/deployment-settings/components/threshold-value-display";
import {
  describeThresholdUnits,
  getThresholdSeverityLabel,
  getThresholdUnitLabel,
  THRESHOLD_SEVERITIES,
  type ThresholdConfig,
} from "@/features/deployment-settings/model/admin-settings";

interface ThresholdConfigCardProps {
  config: ThresholdConfig;
}

export function ThresholdConfigCard({ config }: ThresholdConfigCardProps) {
  return (
    <ConsoleSurface sx={{ p: { xs: 2, md: 2.5 } }}>
      <Stack spacing={2}>
        <SectionHeading component="h3">{config.name}</SectionHeading>
        <Box>
          <Typography variant="body2" color="text.secondary">
            {config.metricLabel}
          </Typography>
          <Chip label={config.scopeLabel} size="small" variant="outlined" sx={{ mt: 1 }} />
        </Box>

        {config.thresholds.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No thresholds configured.
          </Typography>
        ) : (
          <>
            <Box
              sx={{
                display: { xs: "none", sm: "grid" },
                gridTemplateColumns: "minmax(120px, 1fr) minmax(0, 1fr) minmax(120px, 1fr)",
                gap: 1,
                px: 1.25,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Severity Level
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Value
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Unit
              </Typography>
            </Box>
            <Box
              component="ul"
              aria-label={`${config.name} threshold values`}
              sx={{ display: "flex", flexDirection: "column", gap: 1.25, p: 0, m: 0 }}
            >
              {THRESHOLD_SEVERITIES.map((severity) => {
                const threshold = config.thresholds.find((item) => item.severity === severity);
                if (threshold == null) {
                  return null;
                }

                return (
                  <Box
                    component="li"
                    key={severity}
                    sx={(theme) => ({
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "minmax(120px, 1fr) minmax(0, 1fr) minmax(120px, 1fr)",
                      },
                      gap: 1,
                      alignItems: "center",
                      p: 1.25,
                      borderRadius: `${theme.shape.borderRadius}px`,
                      backgroundColor: alpha(theme.palette.common.white, 0.03),
                      border: `1px solid ${alpha(theme.palette.common.white, 0.07)}`,
                    })}
                  >
                    <Box sx={{ justifySelf: "start" }}>
                      <StatusBadge
                        severity={severity === "warning" ? "warning" : severity}
                        label={getThresholdSeverityLabel(severity)}
                      />
                    </Box>
                    <ThresholdValueDisplay threshold={threshold} />
                    <Box>
                      <Typography variant="body2">
                        {getThresholdUnitLabel(threshold.unit)}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </>
        )}

        <Typography variant="caption" color="text.secondary">
          {config.availableUnits.length === 1
            ? `Values are configured in ${describeThresholdUnits(config.availableUnits)}.`
            : "Choose people for an absolute value or percentage of this area’s capacity."}
        </Typography>
      </Stack>
    </ConsoleSurface>
  );
}
